import { ReactElement, lazy, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router';

import appPage from '../../../hoc/appPage';
import dataModel from '../../../models/data';
import toastsModel, { Preset } from '../../../models/toasts';
import {
  isMagicLinkEnabled,
  isOtpLoginEnabled
} from '../../../lib/emailLoginDecider';
import { usePartnerConfig } from '../../../contexts/partnerConfig';
import { Headers } from '../../../components/Headers';
import { FastOnboardForm } from '../../../forms/FastOnboardForm';
import { hasSeenWelcome } from '../../../lib/ab-tests/hasSeenWelcome';
import { WelcomeSplashVariant } from '../../../interfaces/WelcomeSplashVariant';
import { welcomePageNavigateArgs } from '../../../lib/ab-tests/welcomePageNavigateArgs';

import './styles.scss';

const UnavailableView = lazy(() => import('../../../views/UnavailableView'));

function FastOnboardPage(): ReactElement {
  const {
    brandingInfo: { clientId },
    welcomeVariant
  } = dataModel;
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const [canOnboard, setCanOnboard] = useState(true);
  const { config } = usePartnerConfig();
  const {
    oauthConsentTosLink = '',
    oauthConsentPpLink = '',
    oauthConsentNotice
  } = config?.myAccount || {};

  useEffect(() => {
    if (!config) return;

    // allow for non sdk env, js sdk and only whitelisted client ids (same logic as magic link/OTP)
    const allowOnboardFeatureClientId =
      isMagicLinkEnabled(config?.myAccount) ||
      isOtpLoginEnabled(config.myAccount);

    // TODO: remove temporary code after conditional onboard feature is not needed
    if (!allowOnboardFeatureClientId) {
      toastsModel.add({
        text: 'Onboard feature not available.',
        preset: Preset.Danger
      });
    }
    // remove end

    // TODO: when all clients provides us with 2 links, we can make this an "&&" condition
    const haveRequiredLinks = oauthConsentTosLink || oauthConsentPpLink;

    // we will not be able to determine tos link existence without `client_id`
    // hence showing this error only when we can determine it and the value is still missing
    if (clientId && !haveRequiredLinks) {
      toastsModel.add({
        text: t('FastOnboardForm.toast.missingClientTosOrPpLink'),
        preset: Preset.Danger
      });
    }

    setCanOnboard(Boolean(haveRequiredLinks && allowOnboardFeatureClientId));
  }, [config]);

  if (
    !hasSeenWelcome({ search }) &&
    welcomeVariant === WelcomeSplashVariant.SHOW
  ) {
    return (
      <Navigate to={welcomePageNavigateArgs({ pathname, search })} replace />
    );
  }
  if (!canOnboard) return <UnavailableView />;

  return (
    <div className="fast-onboard-page">
      <Headers backButtonBarOnly={true} />
      <FastOnboardForm
        oauthConsentTosLink={oauthConsentTosLink}
        oauthConsentPpLink={oauthConsentPpLink}
        oauthConsentNotice={oauthConsentNotice}
      />
    </div>
  );
}

export default appPage(FastOnboardPage);
